import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import * as AF from "src/types/formTemplate";
import { SchoolSelectionItem } from "./formik";
export * from "./formik";

export function findRelatedQuestionVisibilityByAnswerId(
  questions: readonly AF.V2.Question[],
  questionId: string,
  answerId: string
): AF.V2.Question[] {
  const relatedQuestions: AF.V2.Question[] = [];

  // Loop through all questions to find those with visibility rules dependent on the target question
  for (const question of questions) {
    // Skip the question if it doesn't have visibility rules
    if (!question.visibility) continue;

    let isRelated = false;

    // Check allOf rules
    if ("allOf" in question.visibility && question.visibility.allOf) {
      isRelated = question.visibility.allOf.some((rule) => {
        return rule.rules.some((condition) => {
          return (
            condition.dataType === "questionKey" &&
            condition.property === questionId &&
            condition.operator === "IN" &&
            (Array.isArray(condition.value)
              ? condition.value.includes(answerId)
              : condition.value === answerId)
          );
        });
      });
    }

    // Check anyOf rules if not already found to be related
    if (
      !isRelated &&
      "anyOf" in question.visibility &&
      question.visibility.anyOf
    ) {
      isRelated = question.visibility.anyOf.some((rule) => {
        return rule.rules.some((condition) => {
          return (
            condition.dataType === "questionKey" &&
            condition.property === questionId &&
            condition.operator === "IN" &&
            (Array.isArray(condition.value)
              ? condition.value.includes(answerId)
              : condition.value === answerId)
          );
        });
      });
    }

    // If the question has a related visibility rule, add it to the result array
    if (isRelated) {
      relatedQuestions.push(question);
    }
  }

  return relatedQuestions;
}

export function findRelatedQuestionVisibilityByQuestionId(
  questions: readonly AF.V2.Question[],
  questionId: string
): AF.V2.Question[] {
  const relatedQuestions: AF.V2.Question[] = [];

  // Loop through all questions to find those with visibility rules dependent on the target question
  for (const question of questions) {
    // Skip the question if it doesn't have visibility rules
    if (!question.visibility) continue;

    let isRelated = false;

    // Check allOf rules
    if ("allOf" in question.visibility && question.visibility.allOf) {
      isRelated = question.visibility.allOf.some((rule) => {
        return rule.rules.some((condition) => {
          return (
            condition.dataType === "questionKey" &&
            condition.property === questionId
          );
        });
      });
    }

    // Check anyOf rules if not already found to be related
    if (
      !isRelated &&
      "anyOf" in question.visibility &&
      question.visibility.anyOf
    ) {
      isRelated = question.visibility.anyOf.some((rule) => {
        return rule.rules.some((condition) => {
          return (
            condition.dataType === "questionKey" &&
            condition.property === questionId
          );
        });
      });
    }

    // If the question has a related visibility rule, add it to the result array
    if (isRelated) {
      relatedQuestions.push(question);
    }
  }

  return relatedQuestions;
}

export function findInvalidQuestionVisibility(
  questions: readonly AF.V2.Question[]
): uuid[] {
  const invalidIds: string[] = [];

  // Get all question ids in the form
  const availableQuestionIds = new Set(
    questions
      .filter((question) => !question.isArchived)
      .map((question) => question.id)
  );

  // Create a map of question ids to valid option ids (excluding archived options)
  const questionOptionsMap = new Map<string, Set<string>>();
  for (const question of questions) {
    if ("options" in question && question.id !== undefined) {
      questionOptionsMap.set(
        question.id,
        new Set(
          question.options
            .filter((option) => !option.isArchived)
            .flatMap((option) => (option.id ? [option.id] : []))
        )
      );
    }
  }

  // Iterate through each question to check for invalid visibility rules
  for (const question of questions) {
    // Skip questions without visibility rules
    if (!question.visibility) continue;

    const rules =
      ("allOf" in question.visibility
        ? question.visibility.allOf
        : undefined) ??
      ("anyOf" in question.visibility
        ? question.visibility.anyOf
        : undefined) ??
      ("oneOf" in question.visibility
        ? question.visibility.oneOf
        : undefined) ??
      [];

    for (const rule of rules) {
      for (const condition of rule.rules) {
        // Check if the condition references a question id
        if (condition.dataType === "questionKey") {
          // Check if the referenced question exists
          if (!availableQuestionIds.has(condition.property)) {
            invalidIds.push(question.id);
            break; // One invalid rule is enough to mark the question
          }

          // Check if the value references valid options
          const validOptions = questionOptionsMap.get(condition.property);
          if (validOptions) {
            const values = Array.isArray(condition.value)
              ? condition.value
              : [condition.value];

            const hasInvalidValue = values.some(
              (value) => !validOptions.has(value)
            );
            if (hasInvalidValue) {
              invalidIds.push(question.id);
              break; // One invalid rule is enough to mark the question
            }
          }
        }
      }
    }
  }

  return invalidIds;
}

const SUPPORTED_QUESTION_TYPES: AF.V2.QuestionType[] = [
  AF.V2.QuestionTypes.SingleSelect,
  AF.V2.QuestionTypes.MultiSelect,
  AF.V2.QuestionTypes.Grades,
];
export function toQuestionItems(
  sectionType: AF.V2.SectionType,
  questions: readonly AF.V2.Question[]
): Item[] {
  const items: Item[] =
    sectionType === AF.V2.SectionTypes.PreRankingSection
      ? []
      : [SchoolSelectionItem];
  for (const question of questions) {
    if (
      !SUPPORTED_QUESTION_TYPES.includes(question.type) ||
      question.isArchived
    ) {
      continue;
    }

    items.push({ key: question.id, label: question.question });
  }

  return items;
}

type Option = {
  id: string;
  label: string;
};
type SchoolItem = {
  id: string;
  label: string;
};
export function toAnswerOptions(
  sectionType: AF.V2.SectionType,
  selectedKey: string | undefined,
  questions: readonly AF.V2.Question[],
  schools: SchoolItem[]
): Option[] | null {
  if (selectedKey === undefined) {
    return null;
  }

  if (
    sectionType === "PreRankingSection" &&
    selectedKey === SchoolSelectionItem.key
  ) {
    return null;
  }

  if (selectedKey === SchoolSelectionItem.key) {
    return schools.map((school) => ({
      id: school.id,
      label: school.label,
    }));
  }

  const selectedQuestion = questions.find((q) => q.id === selectedKey) ?? null;
  if (selectedQuestion === null) {
    return null;
  }

  if (!("options" in selectedQuestion)) {
    return null;
  }

  const options: Option[] = [];
  for (const option of selectedQuestion.options) {
    if (option.id === undefined) {
      continue;
    }
    // Exclude archived options
    if (option.isArchived) {
      continue;
    }
    options.push({
      id: option.id,
      label: option.label,
    });
  }

  return options;
}
